window.MulberryPDPState = {
  // Set up state variable to hold the selected offer
  selectedOffer: null,

  // This stores the variant ID of the last added insurance product on mobile.
  // This is needed to know what line item to remove when unchecking the
  // mulberry checkbox. This is reset every time a different mattress variant
  // is selected.
  mobileLastAddedMulberryVarID: null,
  isNurturePage: window.NolahProductJSON.product.title.includes('Nurture'),
};

window.MulberryMUtils = {
  /**
   * Nurture costs $0.99 so we don't want it to say $0.
   * @param price The cost in dollars (59.00), not cents.
   */
  formatWarrantyPrice(price) {
    const formattedPrice = window.MulberryPDPState.isNurturePage
      ? price
      : Math.round(price);
    return '$' + formattedPrice;
  },

  /**
   * Helper for getting a formatted line item object to add in a POST call.
   * @param warranty The warranty object from MulberryPDPState.selectedOffer
   * @param quantity How many to add
   * @param mattressVar The mattress variant that it should be linked to in the line item props.
   * @returns 
   */
   getWarrantyItemObjToAdd(warranty, quantity, mattressVar) {
    return {
      id: warranty.warrantyVariantId,
      quantity,
      properties: {
        _variantId: mattressVar.id,
        _varTitle: mattressVar.title, // Used only for showing the title in the sidecart
        _product: window.NolahProductJSON.product.title,
        _warranty_duration_months: warranty.duration_months,
      },
    };
  }
}

/**
 * Variant IDs, prices, and durations are hardcoded and accessible via this
 * manager.
 */
window.MulberryOfferManager = {
  /**
   * @param variant Typical variant object from shopify. Needs title and name.
   * @returns Object with warranty variant ID, duration, and cost inside.
   */
  getWarranty(variant) {
    // Nurture product has a special 99 cent warranty variant
    if (variant.name.includes('Nurture 10')) {
      return {
        "warrantyVariantId": 39695611134031,
        "duration_months": "120",
        "cost": "0.99",
      }
    }

    // Otherwise, check for the size to get the variant.
    const largeSizeGroup = /(Queen|King|Cal King)( \/ (Plush|Firm|Luxury Firm))?/;
    const mediumSizeGroup = /(Twin|Twin XL|Full)( \/ (Plush|Firm|Luxury Firm))?/;
    if (largeSizeGroup.test(variant.title)) {
      return {
        "warrantyVariantId": 39695611560015,
        "duration_months": "120",
        "cost": "29.00",
      }
    } else if (mediumSizeGroup.test(variant.title)) {
      return {
        "warrantyVariantId": 39695611396175,
        "duration_months": "120",
        "cost": "29.00",
      }
    }
  }
}

/**
 * Handles the Mulberry popup.
 * Takes in the WarrantyCTABtn object in order to trigger its click handler,
 * which is the class that represents the main cta button you see on the form.
 * 
 * Dependencies: GlobalNPopup, template in product-mattress-popup-templates.
 */
class WarrantyInfoPopup {
  constructor(warrantyCTABtn) {
    this.warrantyCTABtn = warrantyCTABtn;

    this.cacheElements();
    this.attachListeners();
  }

  cacheElements() {
    this.container = document.querySelector('.MulberryUpsellArea');
    this.openPopupHandler = this.openPopupHandler.bind(this);
    this.popupTrigger = this.container.querySelector('.MulberryUpsellArea__WhatsCovered');

    // These are the price label elements of the cta button to add the warranty
    // nearby in the mulberry upsell area (outside the popup), used to reference
    // what prices to show in the popup.
    this.outsideCTAPrice = this.container.querySelector('.MulberryUpsellArea__Price');
    this.outsideCTAComparePrice =
      this.container.querySelector('.MulberryUpsellArea__PriceTag .StrikeLabel');
  }

  attachListeners() {
    this.popupTrigger.addEventListener('click', this.openPopupHandler.bind(this));
  }

  openPopupHandler(e) {
    const contentsEl = document.querySelector('#product-mattress-mulberry-info');
    GlobalNPopup.openWithContents('pm-mulberry-info', contentsEl.innerHTML);

    // Set up the btns inside the newly populated popup
    this.setUpPopupContents(GlobalNPopup.popup);
  }

  setUpPopupContents(popupEl) {
    const addBtn = popupEl.querySelector('.PMMulberryInfo__AddBtn');
    addBtn.classList.toggle(
      'PMMulberryInfo__AddBtn--added',
      window.MulberryPDPState.selectedOffer
    );

    addBtn.addEventListener('click', e => {
      // Disable the button immediately (no need to reenable later)
      addBtn.disabled = true;

      this.warrantyCTABtn.handleAddBtnClick();

      GlobalNPopup.close();
    });

    // Sync the prices within the popup to accurate numbers.
    // We reference the outside cta label for simplicity.
    const priceEl = popupEl.querySelector('.PMMulberryInfo__Price');
    priceEl.innerText = this.outsideCTAPrice.innerText;
    const comparePriceEl = popupEl.querySelector('.PMMulberryInfo__PriceLabel .StrikeLabel');
    comparePriceEl.innerText = this.outsideCTAComparePrice.innerText;

    // Initialize the view FAQs button that reveals the FAQs area
    this.initViewFAQsBtn(popupEl);

    // Initializes the drawers within
    this.initDrawers(popupEl);
  }

  initViewFAQsBtn(popupEl) {
    const viewFAQsBtn = popupEl.querySelector('.PMMulberryInfo__ViewMore');
    const drawersWrapper = popupEl.querySelector('.PMMulberryDrawers');
    viewFAQsBtn.addEventListener('click', () => {
      window.NUtils.toggleBooleanAttribute(viewFAQsBtn, 'aria-expanded');
      this.slideToggleFAQsArea(drawersWrapper, viewFAQsBtn);
    });
  }

  slideToggleFAQsArea(drawers, viewFAQsBtn) {
    const compStyles = window.getComputedStyle(drawers);
    const height = compStyles.getPropertyValue('height');
    const heightValue = parseFloat(height);
    // Less than or equal to 1 because it has a 1px border
    if (heightValue <= 1) {
      window.NUtils.slideDown(drawers);
      viewFAQsBtn.innerHTML = 'Hide Frequently Asked Questions';
    } else {
      // Set the height again to ensure that it animates (in case it was 'auto')
      drawers.style.height = heightValue + 'px';

      // Request animation frame or else the above line will not have enough
      // time to recognize the height to animate from. Without requesting animation
      // frame, it appears that consective calls like 'el.style.height = oldHeight'
      // and el.style.height = newHeight' are batched together and the oldHeight
      // is ignored, causing no transition to happen.
      window.requestAnimationFrame(() => {
        window.NUtils.slideUp(drawers);
        viewFAQsBtn.innerHTML = 'View Frequently Asked Questions';
      })
    }
  }

  initDrawers(popupEl) {
    const triggers = popupEl.querySelectorAll('.PMMulberryDrawers__Trigger');
    const panels = popupEl.querySelectorAll('.PMMulberryDrawers__Panel');
    const drawersWrapper = popupEl.querySelector('.PMMulberryDrawers');

    // Set height values to open panels so that css transitions can happen
    const openPanels = popupEl.querySelectorAll('.PMMulberryDrawers__Panel:not(.PMMulberryDrawers__Panel--startClosed)');
    openPanels.forEach(p => {
      p.style.height = p.scrollHeight + 'px';
    });

    // Set default state by checking for the class 'PMMulberryDrawers__Panel--startClosed'
    const closedPanels = popupEl.querySelectorAll('.PMMulberryDrawers__Panel--startClosed');
    closedPanels.forEach(p => {
      p.style.height = '0px';
      p.classList.remove('PMMulberryDrawers__Panel--startClosed');
    });

    triggers.forEach(trigger => trigger.addEventListener('click', (e) => {
      // Always ensure the wrapping drawers have a height of 'auto' so that they
      // aren't fixed after slide toggling them.
      drawersWrapper.style.height = 'auto';

      const category = trigger.dataset.category;
      const matchingPanel =
        Array.from(panels).find(p => p.dataset.category === category);

      window.NUtils.slideToggle(matchingPanel);

      window.NUtils.toggleBooleanAttribute(trigger, 'aria-expanded');
    }));
  }
}

class WarrantyCTABtn {
  constructor() {
    this.errorMessage = 'Sorry, we may be experiencing a technical issue with our insurance product. Please contact us for help.';
    this.mulberryAddButton = document.querySelector('.MulberryUpsellArea__CTABtn');

    // Update price tag with initial variant
    const currentVariant = window.NolahPDPState.currentVariant;
    const warrantyVariant =
      window.MulberryOfferManager.getWarranty(currentVariant);
    this.updatePriceTag(warrantyVariant.cost);

    // Add click listener to add/remove warranties
    this.mulberryAddButton.addEventListener('click', this.handleAddBtnClick.bind(this));

    // Reset the btn when options change
    document.addEventListener('state:option-changed', this.handleOptionChanged.bind(this));
  }

  handleAddBtnClick() {
    // Deselect the mulberry offer
    if (window.MulberryPDPState.selectedOffer) {
      this.mulberryAddButton.classList.remove('MulberryUpsellArea__CTABtn--added');
      window.MulberryPDPState.selectedOffer = null;

      // We have different logic for mobile that adds the insurance immediately
      if (window.innerWidth > 768) {
        document.dispatchEvent(new CustomEvent('mulberry-warranty-changed'));
      } else {
        this.mulberryAddButton.disabled = true;
        this.handleRemovingMulberryOnMobile()
          .then(() => (this.mulberryAddButton.disabled = false));
      }
    
    // Otherwise, select the mulberry offer
    } else {
      this.mulberryAddButton.classList.add('MulberryUpsellArea__CTABtn--added');
      const currentVariant = window.NolahPDPState.currentVariant;
      const warrantyVariant =
        window.MulberryOfferManager.getWarranty(currentVariant);
      
      window.MulberryPDPState.selectedOffer = warrantyVariant;

      if (window.innerWidth > 768) {
        document.dispatchEvent(new CustomEvent('mulberry-warranty-changed'));
      } else {
        this.mulberryAddButton.disabled = true;
        this.handleAddingMulberryOnMobile()
          .then(() => (this.mulberryAddButton.disabled = false));
      }
    }
  }

  handleOptionChanged() {
    // Reset selection of offer
    window.MulberryPDPState.selectedOffer = null;
    window.MulberryPDPState.mobileLastAddedMulberryVarID = null;
    this.mulberryAddButton.classList.remove('MulberryUpsellArea__CTABtn--added');
    document.dispatchEvent(new CustomEvent('mulberry-warranty-changed'));

    // Update price
    const currentVariant = window.NolahPDPState.currentVariant;
    const warrantyVariant =
      window.MulberryOfferManager.getWarranty(currentVariant);
    this.updatePriceTag(warrantyVariant.cost);
  }

  /**
   * Immediately adds the mulberry insurance to the cart, without syncing
   * quantities in case the mobile user added it before the mattress.
   */
  async handleAddingMulberryOnMobile() {
    // This function is only meant to be run on mobile
    if (window.innerWidth > 768) {
      return;
    }

    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    const currentVariant = window.NolahPDPState.currentVariant;
    const warranty = window.MulberryPDPState.selectedOffer;

    // We add two warranty products if the pseudo split king size is selected.
    const items = [];
    const getWarrantyItem = window.MulberryMUtils.getWarrantyItemObjToAdd;
    if (window.NolahPDPState.pseudoSplitKingSelected) {
      const secondMattress = window.NolahPDPState.getSecondSplitMattressVariant();

      // If the firmnesses are the same, add two of the current variant.
      if (currentVariant.id === secondMattress.id) {
        items.push(getWarrantyItem(warranty, 2, currentVariant));
      
      // If they're not the same, then add each separately with different line item props.
      } else {
        items.push(getWarrantyItem(warranty, 1, currentVariant));
        items.push(getWarrantyItem(warranty, 1, secondMattress));
      }

    // Otherwise just add the warranty as normal
    } else {
      items.push(getWarrantyItem(warranty, 1, currentVariant));
    }

    // Add to cart
    await window.NolahCartManager.addItems(
      items,
      false /* no need to fix qty */,
      true /* silent */
    )
      .catch((err) => {
        console.error('Adding Mulberry on mobile', err);
        window.alert(this.errorMessage);
      });

    // Store the last added variant ID in case we need to remove it
    window.MulberryPDPState.mobileLastAddedMulberryVarID = warranty.warrantyVariantId;

    // Complete the loading bar and refresh + open the cart
    document.dispatchEvent(new CustomEvent('theme:loading:end'));
    document.dispatchEvent(new CustomEvent('sidecart:refresh-and-open'));
  }

  async handleRemovingMulberryOnMobile() {
    // This function is only meant to be run on mobile
    if (window.innerWidth > 768) {
      return;
    }

    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    const addedVarID = window.MulberryPDPState.mobileLastAddedMulberryVarID;
    if (addedVarID) {
      // Remove the last added mulberry variant (on mobile)
      await window.NolahCartManager.removeUpsell(addedVarID);

      // Forget the last added mulberry variant ID
      window.MulberryPDPState.mobileLastAddedMulberryVarID = null;

      // Complete the loading bar and refresh (but do not open sidecart)
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  /**
   * @param price The cost in dollars in a string (0.99).
   */
  updatePriceTag(price) {
    // Account for whether a pseudo split king is selected (2x Twin XL price).
    // Compare at price is hard coded similarly.
    let priceText = parseFloat(price);
    let comparePrice = 139; // In dollars
    if (window.NolahPDPState.pseudoSplitKingSelected) {
      priceText = (priceText * 2).toFixed(2);
      comparePrice = (comparePrice * 2).toString();
    }

    // Update price
    const priceLabel = MulberryMUtils.formatWarrantyPrice(priceText);
    const priceTagEl = document.querySelector('.MulberryUpsellArea__Price');
    priceTagEl.innerText = priceLabel;

    // Update strike price
    const strikePriceEl = document.querySelector('.MulberryUpsellArea__PriceTag .StrikeLabel');
    strikePriceEl.innerHTML = '$' + comparePrice;
  }

  getCurrentWarranty() {
    const currentVariant = window.NolahPDPState.currentVariant;
    return window.MulberryOfferManager.getWarranty(currentVariant);
  }
}

window.MulberryPDPIntegration = {
  init() {
    // Init cta btn
    this.ctaBtn = new WarrantyCTABtn();

    // Init popup
    this.infoPopup = new WarrantyInfoPopup(this.ctaBtn);
  },
}

MulberryPDPIntegration.init();

/**
 * Example event script for collecting warranty variants across each mattress variant
 * into a variable.
 */
// window.MulberryCachedOffers = {
// };
// document.addEventListener('state:option-changed', async (e) => {
//   const selectedVariant = getCurrentlySelectedVariant(this.productJSON.variants);
//   console.log('selected variant', selectedVariant);
//   const offers = await mulberry.core.getWarrantyOffer({
//     id: selectedVariant.id,
//     title: this.productJSON.title,
//     price: selectedVariant.price,
//     detail: this.productJSON
//   });
  
//   // These values affect what 'getWarrantyVariant' returns
//   offers[0].customer_cost = warrantyJSON.price;
//   offers[0].cost = warrantyJSON.price;
//   offers[0].insurer_category = warrantyJSON.insurer_category;

//   const warrantyVariant = await window.mulberryShop.getWarrantyVariant(offers[0]);
//   window.MulberryCachedOffers[selectedVariant.title] = {
//     productVariantId: selectedVariant.id,
//     warrantyVariantId: warrantyVariant.shopify_variant_id,
//     warranty_offer_id: offers[0].warranty_offer_id,
//     duration_months: offers[0].duration_months,
//     customer_cost: offers[0].customer_cost,
//   };
// });

/**
 * The following are example properties that mulberry products have.
 */
// const exampleProperties = {
//   _variantId: 39307024695375, // Required

//   // May not be necessary
//   _warranty_offer_id: '98a7f32a-3f7c-41f4-b9d2-a335dc4e8944',

//   _product: 'Nolah Evolution 15', // Required
//   _warranty_duration_months: '120'
// };